<template>
  <div class="card mb-3">
    <div class="card-body">
      <div
        class="cg-card-divider d-flex justify-content-between align-items-center gap-3"
      >
        <div class="d-flex align-items-center gap-3 min-w-0">
          <h3 class="cg-card-title text-truncate">{{ company.name }}</h3>
          <span
            v-if="company.isReady"
            class="text-success"
            title="All required company details are on file"
            ><font-awesome-icon icon="circle-check"
          /></span>
          <ToneBadge v-else tone="warn" :label="company.missingLabel" />
        </div>
        <button
          v-if="!editing"
          type="button"
          class="btn btn-sm btn-outline-secondary text-nowrap"
          @click="startEditing"
        >
          <font-awesome-icon icon="pencil" class="me-1" />Edit details
        </button>
      </div>

      <!-- Read-only ledger of the beneficiary details, as the cancellation order review shows them. -->
      <div v-if="!editing" class="cg-ledger">
        <div class="cg-ledger-grid">
          <div class="cg-ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              Company registry code
            </div>
            <div class="fw-semibold">
              {{ company.registryCode || "—" }}
            </div>
          </div>
          <div class="cg-ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              Beneficiary type
            </div>
            <div class="fw-semibold">{{ company.beneficiaryType }}</div>
          </div>
          <div class="cg-ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              Statement visibility
            </div>
            <div class="fw-semibold">{{ company.visibility }}</div>
          </div>
        </div>
        <div class="cg-ledger-cell">
          <div class="eyebrow-label eyebrow-label--sm mb-1">
            Beneficiary address
          </div>
          <div class="fw-semibold">{{ company.address || "—" }}</div>
        </div>
        <div class="cg-ledger-cell">
          <div class="eyebrow-label eyebrow-label--sm mb-1">
            Cancellation purpose
          </div>
          <div>{{ company.purpose }}</div>
        </div>
      </div>

      <!-- Edit mode: a plain Bootstrap form on a local draft; nothing lands until Save. -->
      <form v-else class="row g-3" @submit.prevent="save">
        <div class="col-md-6">
          <label class="form-label" :for="`${uid}-name`">Company name</label>
          <input
            :id="`${uid}-name`"
            v-model="draft.name"
            type="text"
            class="form-control"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label" :for="`${uid}-code`"
            >Company registry code</label
          >
          <input
            :id="`${uid}-code`"
            v-model="draft.registryCode"
            type="text"
            class="form-control"
            placeholder="Not on file"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label" :for="`${uid}-type`"
            >Beneficiary type</label
          >
          <select
            :id="`${uid}-type`"
            v-model="draft.beneficiaryType"
            class="form-select"
          >
            <option value="Consumer">Consumer</option>
            <option value="Producer">Producer</option>
            <option value="Trader">Trader</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label" :for="`${uid}-vis`"
            >Statement visibility</label
          >
          <select
            :id="`${uid}-vis`"
            v-model="draft.visibility"
            class="form-select"
          >
            <option value="Public statement">Public statement</option>
            <option value="Private statement">Private statement</option>
          </select>
        </div>
        <div class="col-12">
          <label class="form-label" :for="`${uid}-addr`"
            >Beneficiary address</label
          >
          <input
            :id="`${uid}-addr`"
            v-model="draft.address"
            type="text"
            class="form-control"
            placeholder="Not on file"
          />
        </div>
        <div class="col-12">
          <label class="form-label" :for="`${uid}-purpose`"
            >Cancellation purpose</label
          >
          <input
            :id="`${uid}-purpose`"
            v-model="draft.purpose"
            type="text"
            class="form-control"
          />
        </div>
        <div class="col-12 d-flex gap-2">
          <button type="submit" class="btn btn-sm btn-primary">
            Save changes
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="cancel"
          >
            Cancel
          </button>
        </div>
      </form>

      <div
        class="d-flex justify-content-between align-items-center gap-3 mt-4 mb-2"
      >
        <p class="cg-micro mb-0">Consumption locations · MWh</p>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="$emit('add-meter')"
        >
          <font-awesome-icon icon="plus" class="me-1" />Add consumption location
        </button>
      </div>
      <div class="border rounded overflow-hidden">
        <div class="cg-meter-row bg-body-secondary border-bottom cg-meter-head">
          <span class="cg-micro">Consumption location</span
          ><span class="cg-micro">Electricity supplier</span>
          <span class="cg-micro text-end">Actual to date</span
          ><span class="cg-micro text-end">Estimated</span
          ><span class="cg-micro text-end">2026 total</span><span />
        </div>
        <div
          v-for="mp in company.meterList"
          :key="mp.key"
          class="cg-meter-row border-bottom"
        >
          <div class="min-w-0">
            <p class="mb-0 fw-semibold small text-truncate">{{ mp.name }}</p>
            <p class="mb-0 text-body-secondary small text-truncate">
              {{ mp.mpid || "No identifier" }}
            </p>
          </div>
          <div class="min-w-0">
            <p class="mb-1 small text-truncate">
              {{ mp.supplier || "Not specified" }}
            </p>
            <ToneBadge :tone="mp.sourceTone" :label="mp.sourceLabel" />
          </div>
          <span class="cg-tabular small text-end">{{
            mp.actual ? fmt(mp.actual) : "—"
          }}</span>
          <span class="cg-tabular small text-end">{{
            mp.annual - mp.actual > 0 ? fmt(mp.annual - mp.actual) : "—"
          }}</span>
          <span class="cg-tabular small text-end fw-semibold">{{
            fmt(mp.annual)
          }}</span>
          <button
            type="button"
            class="btn btn-sm btn-link text-body-secondary p-1"
            title="Edit consumption location"
            @click="$emit('edit-meter', mp)"
          >
            <font-awesome-icon icon="pencil" />
          </button>
        </div>
        <p
          v-if="company.meterList.length === 0"
          class="mb-0 text-body-secondary small p-3"
        >
          No consumption locations yet.
        </p>
        <div v-else class="cg-meter-row bg-body-secondary">
          <span class="cg-micro"
            >{{ company.meterList.length }} consumption
            {{
              company.meterList.length === 1 ? "location" : "locations"
            }}</span
          ><span />
          <span class="cg-tabular small text-end">{{
            company.totals.actual ? fmt(company.totals.actual) : "—"
          }}</span>
          <span class="cg-tabular small text-end">{{
            company.totals.estimated ? fmt(company.totals.estimated) : "—"
          }}</span>
          <span class="cg-tabular small text-end fw-semibold">{{
            fmt(company.totals.fy)
          }}</span
          ><span />
        </div>
      </div>

      <div v-if="company.supplierRoute" class="mt-4">
        <p class="cg-micro mb-2">
          Cancellation instructions · one per supplier
        </p>
        <div class="border rounded overflow-hidden">
          <table class="table table-sm mb-0 align-middle small">
            <thead>
              <tr>
                <th class="cg-micro ps-3">Electricity supplier</th>
                <th class="cg-micro">Consumption locations</th>
                <th class="cg-micro text-end">2026 volume</th>
                <th class="cg-micro pe-3">Status</th>
              </tr>
            </thead>
            <tbody class="cg-tabular">
              <tr v-for="g in company.supplierGroups" :key="g.supplier">
                <td class="fw-semibold ps-3">{{ g.supplier }}</td>
                <td class="text-body-secondary">{{ g.meters }}</td>
                <td class="text-end fw-semibold">{{ fmt(g.volume) }}</td>
                <td class="pe-3">
                  <ToneBadge :tone="g.statusTone" :label="g.status" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CompanyEdit } from "@/compliance_groups/fixture/complianceGroupsTypes";
import type {
  CompanyView,
  MeterRowView,
} from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import { reactive, ref, useId } from "vue";
import ToneBadge from "./ToneBadge.vue";

const props = defineProps<{ company: CompanyView }>();
const emit = defineEmits<{
  /** The fields the user changed, after Save. Cancel emits nothing. */
  save: [changes: CompanyEdit];
  "add-meter": [];
  "edit-meter": [meter: MeterRowView];
}>();

const uid = useId();
const editing = ref(false);
const EDITABLE = [
  "name",
  "registryCode",
  "beneficiaryType",
  "visibility",
  "address",
  "purpose",
] as const satisfies readonly (keyof CompanyEdit)[];
const snapshot = (): Required<CompanyEdit> =>
  Object.fromEntries(
    EDITABLE.map((field) => [field, props.company[field]])
  ) as Required<CompanyEdit>;
const draft = reactive<Required<CompanyEdit>>(snapshot());

const startEditing = () => {
  Object.assign(draft, snapshot());
  editing.value = true;
};
const cancel = () => {
  editing.value = false;
};
const save = () => {
  const changes: CompanyEdit = {};
  for (const field of EDITABLE) {
    if (draft[field] !== props.company[field]) changes[field] = draft[field];
  }
  if (Object.keys(changes).length) emit("save", changes);
  editing.value = false;
};
</script>

<style scoped lang="scss">
// Same ledger treatment as the cancellation order review: hairlines drawn by the grid gap, one
// full-width cell per long field.
$rule: var(--phoenix-card-border-color);

.cg-ledger {
  border: 1px solid $rule;
  border-radius: 0.5rem;
  overflow: hidden;
}
.cg-ledger-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1px;
  background: $rule;

  > .cg-ledger-cell {
    background: var(--phoenix-emphasis-bg);
  }
}
.cg-ledger > .cg-ledger-cell {
  border-top: 1px solid $rule;
}
.cg-ledger-cell {
  min-width: 0;
  padding: 0.75rem 1rem;
}
@media (max-width: 767.98px) {
  .cg-ledger-grid {
    grid-template-columns: 1fr;
  }
}
.cg-meter-row {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) 96px 88px 96px 34px;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
}
.cg-meter-head {
  padding: 8px 12px;
}
</style>
